import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatRadioModule } from '@angular/material/radio';

import { AdminUsersService } from '../../../../core/admin/admin-users.service';
import {
  AdminUser,
  MCP_ACCESS_OPTIONS,
  McpAccessTier,
} from '../../../../core/admin/admin-users.types';
import { IconComponent } from '../../../../shared/icon/icon';

export interface McpAccessModalData {
  readonly user: AdminUser;
}

/**
 * Grant or revoke a user's MCP (Model Context Protocol) access from
 * /admin/users. A deliberate, separate confirmation surface — granting MCP
 * access can export CapX data (and at HIGH, unredacted PII) to an external AI
 * client, so it sits behind its own modal rather than an inline toggle.
 *
 * Open with `MatDialog.open(McpAccessModalComponent, { data })`. On a saved
 * change the dialog closes with the updated `AdminUser`; cancel closes with
 * `undefined`.
 */
@Component({
  selector: 'app-mcp-access-modal',
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    MatRadioModule,
    IconComponent,
  ],
  templateUrl: './mcp-access-modal.html',
  styleUrl: './mcp-access-modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class McpAccessModalComponent {
  private readonly service = inject(AdminUsersService);
  private readonly dialogRef =
    inject<MatDialogRef<McpAccessModalComponent, AdminUser | undefined>>(
      MatDialogRef,
    );
  protected readonly data = inject<McpAccessModalData>(MAT_DIALOG_DATA);

  protected readonly options = MCP_ACCESS_OPTIONS;
  // The currently-chosen grant, seeded from the user's existing tier.
  protected readonly selected = signal<McpAccessTier | null>(
    this.data.user.mcp_access_tier,
  );
  protected readonly saving = signal(false);
  protected readonly error = signal(false);

  /** True when the selection differs from the user's stored tier. */
  protected get dirty(): boolean {
    return this.selected() !== this.data.user.mcp_access_tier;
  }

  protected select(value: McpAccessTier | null): void {
    this.selected.set(value);
  }

  protected cancel(): void {
    this.dialogRef.close(undefined);
  }

  protected save(): void {
    if (!this.dirty || this.saving()) {
      return;
    }
    this.saving.set(true);
    this.error.set(false);

    const tier = this.selected();
    const call =
      tier === null
        ? this.service.revokeMcpAccess(this.data.user.id)
        : this.service.setMcpAccess(this.data.user.id, tier);

    call.subscribe({
      next: (updated) => this.dialogRef.close(updated),
      error: () => {
        this.saving.set(false);
        this.error.set(true);
      },
    });
  }
}
